import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Form, Row } from 'react-bootstrap';
import Swal from 'sweetalert2';
import ToggleSwitch from 'react-switch';

import Container from 'components/shared/Container';
import ThirdPartyDropdown from 'components/shared/ThirdPartyDropdown';
import { DATA_STREAM } from 'lib/networking/endpoints';
import { DATA_STREAM_ENTITY } from 'lib/enums/DataStreamEnum';
import { grape } from 'lib/css/colors';
import usePut from 'lib/hooks/usePut';

function DataStreamSettingsTab({ dataStream, onUpdate }) {
  const isMultiVendor = dataStream?.entity_type === DATA_STREAM_ENTITY.MULTI_VENDOR;
  const currentThirdParty = dataStream?.multiple_entities?.third_party || null;

  const [considerThirdParty, setConsiderThirdParty] = useState(!!currentThirdParty);
  const [selectedThirdParty, setSelectedThirdParty] = useState(
    currentThirdParty ? { label: currentThirdParty, value: currentThirdParty } : null,
  );

  const { putData: updateDataStream, loading: saving } = usePut(
    DATA_STREAM,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Settings updated successfully',
        title: 'Success',
      });
      if (onUpdate) {
        onUpdate();
      }
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: error?.response?.data?.message || 'Failed to update settings',
        title: 'Error',
      }),
  );

  // Reset state when dataStream changes
  useEffect(() => {
    const thirdParty = dataStream?.multiple_entities?.third_party || null;
    setConsiderThirdParty(!!thirdParty);
    setSelectedThirdParty(thirdParty ? { label: thirdParty, value: thirdParty } : null);
  }, [dataStream]);

  const handleSave = () => {
    const thirdPartyValue = considerThirdParty ? selectedThirdParty?.value : null;
    updateDataStream({
      data_stream_id: dataStream.id,
      third_party: thirdPartyValue,
    });
  };

  const hasChanges = () => {
    const currentValue = dataStream?.multiple_entities?.third_party || null;
    const newValue = considerThirdParty ? selectedThirdParty?.value : null;
    return currentValue !== newValue;
  };

  return (
    <Container className="mt-4">
      <Row>
        <Col md={8}>
          <h5>Data Stream Settings</h5>
          <p className="text-muted mt-3">
            {`Data stream level settings can be configured here.
            For sync job specific settings like filtering options,
            please configure them when creating or editing sync jobs.`}
          </p>
        </Col>
      </Row>
      {isMultiVendor && (
        <>
          <Row className="mt-4">
            <Col md={6}>
              <Form.Group>
                <div className="d-flex align-items-center">
                  <Form.Label className="mb-0 mr-3">Consider Third Party</Form.Label>
                  <ToggleSwitch
                    checked={considerThirdParty}
                    onChange={checked => {
                      setConsiderThirdParty(checked);
                      if (!checked) {
                        setSelectedThirdParty(null);
                      }
                    }}
                    onColor={grape}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    height={20}
                    width={40}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          {considerThirdParty && (
            <Row className="mt-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Third Party</Form.Label>
                  <ThirdPartyDropdown
                    value={selectedThirdParty}
                    onChange={e => setSelectedThirdParty(e)}
                    skip={!considerThirdParty}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
          <Row className="mt-4">
            <Col md={6}>
              <Button variant="primary" onClick={handleSave} disabled={saving || !hasChanges()}>
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}

DataStreamSettingsTab.propTypes = {
  dataStream: PropTypes.object.isRequired,
  onUpdate: PropTypes.func,
};

export default DataStreamSettingsTab;
